import { Card, CardHeader, TrendPill } from '../../../components/ui/Card';
import { ErrorState, LoadingState } from '../../../components/ui/States';
import { useCsmLeaderboards, useCsmOverview, useCsmPortfolioTrends, useCsmRoster } from '../../../hooks/useDashboard';
import { SyncedAt } from '../../../components/ui/InfoTip';
import { useMemo } from 'react';
import { RosterBridge } from './RosterBridge';
import { UsageOverview } from './UsageOverview';
import { formatDayMonth, formatDayMonthTime } from '../../../utils/dateFormat';
import { formatCompact, formatNumber } from '../../../utils/numberFormat';
import { CHART_SERIES } from '../../../components/ui/chart';
import { shiftDaysISO, todayISO } from './period';
import { combinedLine, signedPct, workspaceShareNote } from './usageNotes';

/**
 * A change on a tiny base ("+2935.9%") says nothing, so anything past 10× is
 * shown as ">10×" with the exact figure in the title.
 */
function deltaChange(pct: number | null): { value: string; trend: 'up' | 'down' | 'neutral' } | undefined {
  // No prior period: no pill, rather than a bare arrow with a note in it.
  if (pct === null) return undefined;
  const trend = pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';
  if (pct >= 900) return { value: '>10× vs previous period', trend };
  const rounded = Math.round(pct);
  return {
    value: `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${Math.abs(rounded)}% vs previous period`,
    trend: rounded === 0 ? 'neutral' : trend,
  };
}

/** A leaderboard row's change: the same tinted pill on every row of the top 10. */
function DeltaText({ pct, title, neutral }: { pct: number | null | undefined; title?: string; neutral?: boolean }) {
  // Undefined: the previous window isn't in the data, so no change is shown at all.
  if (pct === undefined) return null;
  if (pct === null) return <span className="text-body-xs text-text-tertiary">new</span>;
  const rounded = Math.round(pct);
  const label = pct >= 900 ? '>10×' : `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${Math.abs(rounded).toLocaleString('en-US')}%`;
  return (
    <span title={title ? `${title}: ${signedPct(pct)}` : signedPct(pct)}>
      <TrendPill trend={rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'neutral'} tone={neutral ? 'neutral' : undefined}>
        {label}
      </TrendPill>
    </span>
  );
}

function RankedList({
  title,
  description,
  rows,
  neutralDeltas,
}: {
  title: string;
  description?: string;
  /** Grey change pills: login events swing with Auth0 token refreshes, so their rise is not good news. */
  neutralDeltas?: boolean;
  rows: Array<{
    key: string;
    label: string;
    sublabel?: string;
    value: number;
    /** Shown instead of the compact number, e.g. "12 days". */
    valueText?: string;
    deltaPct: number | null | undefined;
    deltaTitle?: string;
  }>;
}) {
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0);
  return (
    <Card padding="sm">
      <CardHeader title={title} description={description} />
      {rows.length === 0 ? (
        <p className="text-body-sm text-text-tertiary">No activity in this period.</p>
      ) : (
        <ol className="space-y-2">
          {rows.map((row, i) => (
            <li key={row.key} className="text-body-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 gap-1.5">
                  <span className="w-6 flex-none text-text-muted tabular-nums">{i + 1}.</span>
                  {/* Name and its sub-line wrap rather than truncate ("ISS Facility Services - North America"). */}
                  <div className="min-w-0">
                    <p className="break-words font-medium text-text-primary">{row.label}</p>
                    {row.sublabel && <p className="break-words text-body-xs text-text-tertiary">{row.sublabel}</p>}
                  </div>
                </div>
                <span className="flex flex-none items-center gap-2 whitespace-nowrap">
                  <span className="tabular-nums" title={formatNumber(row.value)}>
                    {row.valueText ?? formatCompact(row.value)}
                  </span>
                  <DeltaText pct={row.deltaPct} title={row.deltaTitle} neutral={neutralDeltas} />
                </span>
              </div>
              <div className="mt-1.5 h-1 rounded-full bg-surface-tertiary">
                <div
                  className="h-1 rounded-full"
                  style={{
                    backgroundColor: CHART_SERIES[0],
                    width: max > 0 ? `${Math.max((row.value / max) * 100, 2)}%` : '0%',
                  }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

/** "No trend: Auth0 history starts 13 Mar" — said instead of a pill when the previous window isn't in the data. */
function noTrendSub(covered: boolean | undefined, from: string | null | undefined, what: string): string | null {
  if (covered !== false) return null;
  return `No trend: ${what} history starts ${from ? formatDayMonth(`${from}T00:00:00Z`) : 'later'}`;
}

/** CSM › Usage › Classic platform: portfolio KPIs, daily trends and leaderboards. */
export function InsightsTab({ days }: { days: number }) {
  const trends = useCsmPortfolioTrends({ days });
  const leaderboards = useCsmLeaderboards({ days });
  // Shared with Customers (same query key), so a company that is one of several
  // workspaces of a CSM-list entry can say so: Usage counts the workspace, Customers the entry.
  const roster = useCsmRoster(true);
  // Same query (and cache entry) as the Customers strip, for the sync line aOS also shows.
  const syncRange = useMemo(() => ({ from: shiftDaysISO(30), to: todayISO() }), []);
  const sync = useCsmOverview({ ...syncRange, status: 'all', search: '', page: 1, pageSize: 1 });
  const syncedAt = (v: string | null | undefined) => (v ? formatDayMonthTime(v) : 'never');

  if (trends.error || leaderboards.error) {
    return (
      <ErrorState
        title="Couldn’t load usage"
        error={trends.error ?? leaderboards.error}
        onRetry={() => {
          void trends.refetch();
          void leaderboards.refetch();
        }}
      />
    );
  }
  if (trends.isLoading) return <LoadingState rows={4} />;
  const tokensCovered = leaderboards.data?.coverage?.previousTokensCovered !== false;
  return (
    <div className="space-y-6">
      <SyncedAt>
        {sync.data
          ? `Synced: production ${syncedAt(sync.data.lastProdSyncAt)} · Auth0 ${syncedAt(sync.data.lastAuth0SyncAt)}`
          : sync.isError
            ? 'Sync times unavailable'
            : '\u00a0'}
      </SyncedAt>
      {trends.data && (
        <>
          <UsageOverview
            activeUsers={{
              value: formatNumber(trends.data.summary.activeUsers),
              change: deltaChange(trends.data.summary.activeUsersDeltaPct),
              sub:
                noTrendSub(trends.data.coverage?.previousActivityCovered, trends.data.coverage?.activityFrom, 'Auth0') ??
                `Last ${days} days · customer users with a login`,
              tooltip:
                "External members of the roster's live customers with a login in the period. SEQUESTO staff are left out, as on Customers. A user in two customer workspaces counts once here and once per company on Customers.",
            }}
            tokens={{
              value: formatCompact(trends.data.summary.tokens),
              change: deltaChange(trends.data.summary.tokensDeltaPct),
              sub:
                noTrendSub(trends.data.coverage?.previousTokensCovered, trends.data.coverage?.tokensFrom, 'token') ??
                `Last ${days} days · roster customers · Classic only`,
              tooltip:
                'Classic platform only, for the live customers on the CSM roster. Customers, Declining and At risk show Classic + aOS together.',
            }}
            activeCustomers={{
              value: formatNumber(trends.data.summary.activeCompanies),
              sub: `Last ${days} days · live roster customers with a login`,
              tooltip:
                'Live customers on the CSM list with at least one customer-user login in the period. Usage › aOS counts workspaces with AI usage instead, on the list or not, so the two numbers differ.',
            }}
            bridge={
              trends.data.bridge && (
                <RosterBridge
                  platform="Classic"
                  total={trends.data.bridge.platformTokens}
                  bridge={trends.data.bridge}
                  days={days}
                  note="Classic tokens carry no user, so they all count, here and on Customers."
                />
              )
            }
            tokensTitle="Daily tokens"
            series={trends.data.series}
          />
        </>
      )}

      {leaderboards.isLoading ? (
        <LoadingState />
      ) : (
        leaderboards.data && (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Ranked by days with a login and members with a login: login events are inflated by token
                refreshes, so they are only a neutral second line, never a headline or a green pill. */}
            <RankedList
              title="Most active users"
              description={`Days with a login, last ${days} days. Second line: company and login events.`}
              rows={[...leaderboards.data.topUsers]
                .sort((a, b) => b.activeDays - a.activeDays || b.events - a.events)
                .map((u) => ({
                  key: u.userId,
                  label: u.name,
                  sublabel: [u.companyNames.join(', '), `${formatNumber(u.events)} login events`].filter(Boolean).join(' · '),
                  value: u.activeDays,
                  valueText: `${formatNumber(u.activeDays)} ${u.activeDays === 1 ? 'day' : 'days'}`,
                  deltaPct: undefined,
                }))}
            />
            <RankedList
              title="Most active companies"
              description={`Members with a login, last ${days} days. Second line: of all members, and login events.`}
              rows={(leaderboards.data.topCompaniesByActiveUsers ??
                [...leaderboards.data.topCompaniesByEvents].sort((a, b) => b.activeMembers - a.activeMembers || b.events - a.events)
              ).map((c) => ({
                key: c.companyId,
                label: c.name,
                sublabel: [
                  `${formatNumber(c.activeMembers)} active of ${formatNumber(c.totalMembers)} members · ${formatNumber(c.events)} login events`,
                  workspaceShareNote(c.companyId, roster.data?.rows ?? []),
                ]
                  .filter(Boolean)
                  .join(' · '),
                value: c.activeMembers,
                deltaPct: undefined,
              }))}
            />
            <RankedList
              title="Top companies by Classic tokens"
              description={`Value and change vs the previous ${days} days: Classic only. A big % is a small base, not a trend. Second line: Classic + aOS, the figure At risk compares.`}
              rows={leaderboards.data.topCompaniesByTokens.map((c) => ({
                key: c.companyId,
                label: c.name,
                sublabel: combinedLine(c.combined, leaderboards.data.combinedError),
                value: c.tokens,
                deltaPct: tokensCovered ? c.tokensDeltaPct : undefined,
                deltaTitle: `Classic tokens, last ${days}d vs the previous ${days}d`,
              }))}
            />
          </div>
        )
      )}
    </div>
  );
}
